import React, { useState, useEffect, useRef } from 'react';

import { 
  Collapse, 
  Table, 
  Row, 
  Col, 
  Button, 
  Divider 
} from 'antd';
import {
  DownOutlined,
  RightOutlined,
  LeftOutlined
} from '@ant-design/icons';



const { Panel } = Collapse;

/**
 * MasterDetailTable component for displaying master-detail data in an accordion
 * 
 * @param {Object} props
 * @param {Object} props.data - The data object containing master and details
 * @param {Object} props.data.master - Master data with key-value pairs
 * @param {Array} props.data.details - Detail data for the table
 * @param {string} props.title - The title of the accordion
 * @param {string} props.uuid - The UUID of the data object
 * @param {number} props.currentVersion - Current version of the data
 * @param {Object} props.versionInfo - Information about all versions
 * @param {Function} props.onVersionChange - Callback when version is changed
 * @param {Function} props.registerComponent - Function to register for updates
 */
const MasterDetailTable = ({ 
  data, 
  title = "Claim Under Review", 
  uuid, 
  currentVersion = 1, 
  versionInfo = {}, 
  onVersionChange,
  registerComponent 
}) => {
  // Destructure the data
  const { master = {}, details = [] } = data || {};
  
  // State to track whether the component is expanded
  const [isExpanded, setIsExpanded] = useState(true);
  
  // Generate columns for the detail table
  const generateColumns = () => {
    if (!details || details.length === 0) return [];
    
    // Get all unique keys from the first object
    const keys = Object.keys(details[0]);
    
    return keys.map(key => ({
      title: key.toUpperCase(),
      dataIndex: key,
      key: key,
      // Format cell content based on key name
      render: (text) => {
        if (key.toLowerCase().includes('date')) {
          return text; // Date formatting can be added here if needed
        }
        return text;
      }
    }));
  };
  
  // Handler for version navigation
  const handleVersionChange = (newVersion) => {
    if (onVersionChange && uuid) {
      onVersionChange(uuid, newVersion);
    }
  };


  //////////  Component data sync from backend ?????????????????
  const [updateCounter, setUpdateCounter] = useState(0);

  // Register this component for updates if the function is provided
  useEffect(() => {
    if (registerComponent && uuid) {
      // Create a refresh function to update this component
      const refreshComponent = () => {
        console.log(`Refreshing Component: ${uuid}`);
        setUpdateCounter(prev => prev + 1);
      };
      
      // Register this component with the provided function
      const unregister = registerComponent(refreshComponent);
      
      // Unregister when the component unmounts
      return unregister;
    }
  }, [registerComponent, uuid]);
  //////////  Component data sync from backend ?????????????????


  
  
  // Check if version navigation should be displayed
  const showVersionNavigation = versionInfo && 
                               versionInfo.available_versions && 
                               versionInfo.available_versions.length > 1;
  
  // Button styles
  const buttonStyle = {
    backgroundColor: '#e0cbff',
    borderColor: '#c29fee',
    color: '#4d2c91',
    fontWeight: 'bold'
  };
  
  return (
    <div style={{ 
      marginBottom: '16px', 
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
      borderRadius: '4px'
    }}>
      <Collapse
        defaultActiveKey={['1']}
        expandIcon={({ isActive }) => {
          setIsExpanded(isActive);
          return isActive ? <DownOutlined /> : <RightOutlined />;
        }}
        className="custom-collapse"
        items={[
          {
            key: '1',
            label: (
              <div style={{ 
                fontWeight: 'bold',
                fontSize: '18px'
              }}>
                {title}
              </div>
            ),
            children: (
              <>
                {/* Master section - Display key-value pairs */}
                {Object.entries(master).map(([key, value], index) => (
                  <div key={key} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <Row>
                      <Col span={8} style={{ fontWeight: 'bold', padding: '8px 0' }}>
                        {key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                      </Col>
                      <Col span={16} style={{ textAlign: 'right', padding: '8px 0' }}>
                        {value}
                      </Col>
                    </Row>
                  </div>
                ))}
                
                {/* Detail section - Display table of items */}
                {details && details.length > 0 && (
                  <>
                    <div style={{ marginTop: '16px', marginBottom: '8px' }}>
                      <div style={{ fontWeight: 'bold' }}>Claim Details</div>
                    </div>
                    
                    <Table
                      columns={generateColumns()}
                      dataSource={details.map((item, index) => ({ ...item, key: index }))}
                      pagination={false}
                      size="small"
                    />
                  </>
                )}
                
                {/* Version navigation */}
                {showVersionNavigation && (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '16px', 
                    paddingTop: '16px',
                    borderTop: '1px solid #f0f0f0'
                  }}>
                    <Button 
                      size="small" 
                      icon={<LeftOutlined />}
                      style={buttonStyle}
                      disabled={currentVersion <= Math.min(...versionInfo.available_versions)}
                      onClick={() => {
                        const versions = versionInfo.available_versions;
                        const currentIndex = versions.indexOf(currentVersion);
                        if (currentIndex > 0) {
                          handleVersionChange(versions[currentIndex - 1]);
                        }
                      }}
                    >
                      Previous
                    </Button>
                    
                    <div>
                      Version {currentVersion} of {versionInfo.total_versions}
                    </div>
                    
                    <Button 
                      size="small" 
                      icon={<RightOutlined />}
                      style={buttonStyle}
                      disabled={currentVersion >= Math.max(...versionInfo.available_versions)}
                      onClick={() => {
                        const versions = versionInfo.available_versions;
                        const currentIndex = versions.indexOf(currentVersion);
                        if (currentIndex < versions.length - 1) {
                          handleVersionChange(versions[currentIndex + 1]);
                        }
                      }}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            )
          }
        ]}
      />
    </div>
  );
};

export default MasterDetailTable;